import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Select, Store, Actions, ofActionDispatched } from '@ngxs/store';
import { BookmarksState } from 'src/app/bookmarks/state/bookmarks.state';
import { Observable, Subject } from 'rxjs';
import { BookmarkModel } from 'src/app/bookmarks/models/bookmark.model';
import {
  LoadBookmarks,
  SortBookmarks,
  CheckBookmark,
  SetCheckedBookmarks,
  ExportBookmarks,
  ExportBookmarksSuccess,
  ExportBookmarksError,
  LoadOrganizationBookmarks,
  LoadCollectionBookmarks,
  SetSelectedCategory,
  ClearBookmarks
} from 'src/app/bookmarks/state/bookmarks.actions';
import { SetTheme } from 'src/app/layout/state/layout.actions';
import { LayoutThemeEnum } from 'src/app/layout/models/layout-theme.enum';
import { OrganizationModel } from '../../../organizations/models/organization.model';
import { SelectOrganization, SelectCollection, GetCollections, SetOrganizations } from 'src/app/organizations/state/organizations.actions';
import { OrganizationsState } from 'src/app/organizations/state/organizations.state';
import { CollectionModel } from 'src/app/organizations/models/collection.model';
import { PublishDialogComponent, PublishDialogData } from 'src/app/publish/components/publish-dialog/publish-dialog.component';
import { MatDialog, MatSnackBar } from '@angular/material';
import { BookmarkSortModel } from 'src/app/bookmarks/models/bookmark-sort.model';
import { AuthState } from 'src/app/auth/state/auth/auth.state';
import { UserProfileModel } from 'src/app/auth/models/user-profile.model';
import { BookmarksService } from 'src/app/bookmarks/bookmarks.service';
import { BookmarkActionsEnum } from 'src/app/bookmarks/components/bookmark-actions/bookmark-actions.component';
import { WidgetOptions } from 'src/app/publish/models/widget-options.model';
import { PublishState } from 'src/app/publish/state/publish.state';
import { PublishService } from 'src/app/publish/publish.service';
import { takeUntil, map, filter, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { GalleryState } from 'src/app/gallery/state/gallery.state';
import { GalleryViewMode } from 'src/app/gallery/models/gallery-view-mode.enum';
import { StoryType } from 'src/app/story/models/story-type.enum';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DashboardComponent implements OnInit, OnDestroy {
  // Bookmarks
  @Select(BookmarksState.getBookmarks)
  bookmarks$: Observable<BookmarkModel[]>;

  @Select(BookmarksState.getCheckedBookmarks)
  checkedBookmarks$: Observable<string[]>;

  @Select(BookmarksState.getLoading)
  loading$: Observable<boolean>;

  @Select(BookmarksState.getExporting)
  exporting$: Observable<boolean>;

  @Select(BookmarksState.getSort)
  sort$: Observable<BookmarkSortModel>;

  // Organizations
  @Select(OrganizationsState.getOrganization)
  organization$: Observable<OrganizationModel>;

  @Select(OrganizationsState.getOrganizations)
  organizations$: Observable<OrganizationModel[]>;

  @Select(OrganizationsState.getSelectedOrganization)
  selectedOrganization$: Observable<OrganizationModel>;

  @Select(OrganizationsState.getCollections)
  collections$: Observable<CollectionModel[]>;

  @Select(OrganizationsState.getSelectedCollection)
  selectedCollection$: Observable<CollectionModel>;

  @Select(AuthState.getUserProfile)
  userProfile$: Observable<UserProfileModel>;

  @Select(GalleryState.getViewMode)
  viewMode$: Observable<GalleryViewMode>;

  private ngUnsubscribe = new Subject();

  get isEmpty(): boolean {
    const isLoading = this.store.selectSnapshot(BookmarksState.getLoading);
    const bookmarks = this.store.selectSnapshot(BookmarksState.getBookmarks);
    return !isLoading && bookmarks.length === 0;
  }

  /**
   * Gets computed selection label
   */
  get userSelectionLabel(): string {
    const selectedOrganization = this.store.selectSnapshot(OrganizationsState.getSelectedOrganization);
    const selectedCollection = this.store.selectSnapshot(OrganizationsState.getSelectedCollection);
    const collections = this.store.selectSnapshot(OrganizationsState.getCollections);

    if (selectedOrganization) {
      if (selectedCollection) {
        return `Team Models - ${selectedCollection.collection_name}`;
      } else {
        return collections && collections.length ? 'Team Models - All' : 'Team Models';
      }
    } else {
      return 'My Models';
    }    
  }

  renderCardCount = 0;

  bookmarks: BookmarkModel[];
  galleryViewMode = GalleryViewMode;

  constructor(
    private store: Store,
    public dialog: MatDialog,
    private bookmarksService: BookmarksService,
    private publishService: PublishService,
    private actions: Actions,
    private snackBar: MatSnackBar,
    private router: Router
  ) {
  }

  ngOnInit() {
    this.store.dispatch(new SetTheme(LayoutThemeEnum.Light)); // #FIXME: Better theme toggling

    // set initial user selection
    this.initUserSelection();

    // handle export actions
    this.actions
      .pipe(
        ofActionDispatched(ExportBookmarksSuccess),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((response) => {
        this.clearSelection();
        this.snackBar.open(`Successfully exported ${response.bookmarks.length} bookmarks.`);
      });

    this.actions
      .pipe(
        ofActionDispatched(ExportBookmarksError),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.snackBar.open(`Error exporting selected bookmarks.`);
      });
  }

  ngOnDestroy() {
    this.store.dispatch(new ClearBookmarks());

    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  initUserSelection() {
    // set selected category
    this.actions
      .pipe(
        ofActionDispatched(LoadBookmarks, LoadOrganizationBookmarks, LoadCollectionBookmarks),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((e) => {
        if (e instanceof LoadOrganizationBookmarks) {
          this.store.dispatch(new SetSelectedCategory('organization'));
        } else if (e instanceof LoadCollectionBookmarks) {
          this.store.dispatch(new SetSelectedCategory('collection'));
        } else {
          this.store.dispatch(new SetSelectedCategory('individual'));
        }
      });

    // populate collections
    this.organization$
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((organization) => {
        if (organization) {
          this.store.dispatch(new GetCollections(organization.id));
        }
      });

    // wait for organization: set selection
    this.organizations$
      .pipe(
        filter((organizations) => !!organizations),
        take(1),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        const selectedCategory = this.store.selectSnapshot(BookmarksState.getSelectedCategory);
        const organization = this.store.selectSnapshot(OrganizationsState.getOrganization);
        const selectedOrganization = this.store.selectSnapshot(OrganizationsState.getSelectedOrganization);
        const selectedCollection = this.store.selectSnapshot(OrganizationsState.getSelectedCollection);

        if (selectedCategory === 'collection' && selectedCollection) { // persist collection
          this.selectCollection(selectedCollection);
        } else if (selectedCategory === 'organization' && selectedOrganization) { // persist organization
          this.selectOrganization(selectedOrganization);
        } else { // default selection
          if (organization && selectedCategory !== 'individual') {
            this.selectOrganization(organization);
          } else {
            this.selectPrivate();
          }
        }
      });
  }

  handleRenderCountChange(count: number) {
    this.renderCardCount = count;
  }

  handleBookmarksSort(sort: BookmarkSortModel) {
    this.sort(sort);
  }

  handleCheckedChange(ids: string[]) {
    this.store.dispatch(new SetCheckedBookmarks(ids));
  }

  handleSelectBookmark(bookmark: BookmarkModel) {
    const isCustomWidget = this.isCustomWidget(bookmark);
    
    if (bookmark.unavailable) { // notify unavailable
      this.viewUnavailableMessage(bookmark);
    } else if (isCustomWidget) { // view in tab
      const url = this.publishService.getWidgetURL(bookmark, bookmark.embed_url, new WidgetOptions());
      this.viewInNewTab(url, bookmark.title);
    } else { // view bookmark
      this.viewBookmark(bookmark);
    }
  }

  handleSelectPrivate() {
    this.selectPrivate();
  }

  handleSelectOrganization(organization: OrganizationModel) {
    this.selectOrganization(organization);
  }

  handleSelectCollection(collection: CollectionModel) {
    this.selectCollection(collection);
  }

  selectPrivate() {
    this.store.dispatch(new SelectOrganization(null));
    this.store.dispatch(new LoadBookmarks(''));
  }

  selectOrganization(organization: OrganizationModel) {
    this.store.dispatch(new SelectOrganization(organization));
    this.store.dispatch(new SelectCollection(null));
    this.store.dispatch(new LoadOrganizationBookmarks(organization.id));
  }

  selectCollection(collection: CollectionModel) {
    this.store.dispatch(new SelectCollection(collection));
    this.store.dispatch(
      new LoadCollectionBookmarks(collection.group_id, collection.collection_id)
    );
  }

  getBookmarkActions(bookmark: BookmarkModel) {
    const isCustomWidget = this.isCustomWidget(bookmark);
    const canEdit = this.canEdit(bookmark);

    let bookmarkActions = [
      BookmarkActionsEnum.CopyLink,
      BookmarkActionsEnum.Embed
    ];

    if (!isCustomWidget) {
      bookmarkActions = [
        ...bookmarkActions,
        BookmarkActionsEnum.View,
        BookmarkActionsEnum.Edit,
        BookmarkActionsEnum.CopyBookmark
      ];
    }

    if (canEdit) {
      bookmarkActions = [
        ...bookmarkActions, 
        BookmarkActionsEnum.Manage,
        BookmarkActionsEnum.Delete
      ];
    } else if (bookmark.favorite) { // enable deleting favorites
      bookmarkActions = [
        ...bookmarkActions,
        BookmarkActionsEnum.Delete
      ];
    }

    // special case: delete orphaned records
    if (bookmark.unavailable) {
      bookmarkActions = [
        BookmarkActionsEnum.Delete
      ];
    }
    
    return bookmarkActions;
  }

  sort(sort: BookmarkSortModel) {
    this.store.dispatch(new SortBookmarks(sort));
  }

  handleEmbedModel(bookmark: BookmarkModel) {
    const dialogRef = this.dialog.open(PublishDialogComponent, {
      width: '90vw',
      data: <PublishDialogData>{
        bookmark: bookmark
      }
    });
    dialogRef.afterClosed().subscribe(result => {

    });
  }
  
  handleCheckChanged(checked: boolean, id: string) {
    this.setChecked(checked, id);
  }

  setChecked(checked: boolean, id: string) {
    this.store.dispatch(new CheckBookmark(id, checked));
  }

  isChecked(id: string) {
    return this.store.selectSnapshot(BookmarksState.getCheckedBookmarks)
      .some((bookmarkId) => bookmarkId === id);
  }

  canEdit(bookmark: BookmarkModel) {
    return this.bookmarksService.canEdit(bookmark);
  }

  handleSelectAllClick() {
    this.selectAll();
  }

  handleClearSelectionClick() {
    this.clearSelection();
  }

  handleExportSelectionClick() {
    this.exportSelected();
  }

  exportSelected() {
    const bookmarks = this.store.selectSnapshot(BookmarksState.getBookmarks);
    const checkedBookmarks = this.store.selectSnapshot(BookmarksState.getCheckedBookmarks);
    const exportBookmarks = bookmarks
      .filter((bookmark) => checkedBookmarks.some((id) => bookmark.id === id));

    // set export options
    const widgetURL = this.store.selectSnapshot(PublishState.getWidgetURL);
    const widgetOptions = this.store.selectSnapshot(OrganizationsState.getWidgetOptions);
    const appFeatures = this.store.selectSnapshot(OrganizationsState.getAppFeatures);
    const offlineEnabled = appFeatures && appFeatures.offlinePkgGen;

    this.store.dispatch(new ExportBookmarks(exportBookmarks, widgetURL, widgetOptions, offlineEnabled));
  }
  
  selectAll() {
    const ids = this.store.selectSnapshot(BookmarksState.getBookmarks)
      .map((bookmark) => bookmark.id);

    this.store.dispatch(new SetCheckedBookmarks(ids));
  }

  clearSelection() {
    this.store.dispatch(new SetCheckedBookmarks([]));
  }

  isCustomWidget(bookmark: BookmarkModel) {
    return this.publishService.isCustomWidget(bookmark.embed_url_path);
  }

  viewBookmark(bookmark: BookmarkModel) {
    const { id, type } = bookmark;
    this.router.navigate(['/view'], { queryParams: { id, type } });
  }

  viewInNewTab(url: string, title: string) {
    const handle = window.open(url);
    if (handle) {
      this.snackBar.open(`"${title}" loaded in a new tab.`);
    } else {
      this.snackBar.open(`Error launching "${title}" in a new tab.`);
    }
  }

  // #TODO[AC]: handle removing unavilable content
  viewUnavailableMessage(bookmark: BookmarkModel) {
  }

  trackBookmark(bookmark: BookmarkModel) {
    return bookmark.id;
  }
}
